import { tracked } from '@glimmer/tracking'
import Service, { service } from '@ember/service'
import { isNone } from '@ember/utils'
import { assert } from '@ember/debug'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import type AuthToken from '@blakeelearning/auth/services/auth-token'

export interface RawGrade {
  id: number
  name: string
  full_name: string
  position: number
}

export interface GradeSetsResponse {
  grade_set: {
    name: string
    country_id: number
    grades: RawGrade[]
  }
}

export interface Grade {
  name: string
  fullName: string
  position: number
}

export default class GradeSetsService extends Service {
  @service authToken!: AuthToken

  @tracked grades: Grade[] = []

  /**
   * This is the name of the loaded gradeset, for example "Australia - ACT,NSW,TAS - 0,K,1,2,3,4,5,6,7,8,9,10,11,12"
   */
  @tracked
  name: Nullable<string> = null

  get gradePositions() {
    return this.grades.map((grade) => grade.position)
  }

  get gradeNames() {
    return this.grades.map((grade) => grade.name)
  }

  async loadGradeSets() {
    const url = join(config.readingEggsV1Url, 'grade_set').href
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: this.authToken.token ?? '',
      },
    })
    if (!response.ok) throw new Error(await response.text())

    const result = (await response.json()) as GradeSetsResponse | undefined
    assert('grade_set must return a result', result)
    if (!result) return result

    const {
      grade_set: { name, grades },
    } = result
    const mappedGrades = grades.map((grade) => ({
      position: grade.position,
      name: grade.name,
      fullName: grade.full_name,
    }))
    this.grades = mappedGrades
    this.name = name

    return result
  }

  /**
   * Find a grade by the grade name
   * Searches by both name and full name.
   * If grade isn't found returns an empty object as the result of
   * findByPosition is destructed in various use cases without checking for a
   * nullish value.
   *
   * @param  {String} name grade name to look for
   * @return {Grade}
   */
  findByName(name: string): Grade | Record<string, never> {
    let grade = this.grades.find((grade) => grade.name === name)

    if (isNone(grade)) {
      grade = this.grades.find((grade) => grade.fullName === name)
    }

    return grade ?? {}
  }

  /**
   * Find a grade by the grade position
   * If grade isn't found returns an empty object as the result of
   * findByPosition is destructed in various use cases without checking for a
   * nullish value.
   *
   * @param  {Number} position grade position to look for
   * @return {Grade}
   */
  findByPosition(position: number): Grade | Record<string, never> {
    const { grades } = this
    const grade = grades.find((grade) => grade.position === position)

    return grade ?? {}
  }

  optionsBy(textKey: keyof Grade) {
    const { grades } = this
    return grades.map(({ position: value, [textKey]: text }) => ({
      text,
      value,
      uid: value,
    }))
  }

  fullNameFromGradeSetByPosition(position: number): string | undefined {
    return this.findByPosition(position)?.fullName
  }

  nameFromGradeSetByPosition(position: number): string | undefined {
    return this.findByPosition(position)?.name
  }
}

declare module '@ember/service' {
  interface Registry {
    'grade-sets': GradeSetsService
  }
}
